import { useDesktopConnectivity } from '@/hooks/desktop/useDesktopConnectivity';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useAuthToken } from '@/hooks/useAuthToken';
import { extractSsoConnectionIdFromAccessToken } from '@heidi/auth/tokens';
import { useIsRegionOnboardingGuardEnabled } from '@heidi/growth/flags';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

/**
 * The new-user onboarding route — the only surface that can set a user's
 * region (via the country picker) and trigger backend user creation.
 */
const ONBOARDING_ROUTE = '/scribe/new-user';

/**
 * Only self-heal inside the core `/scribe` app, where the stranded-user bug
 * actually lands (Kinde's default post-login redirect is `/scribe`). Scoping
 * here keeps the guard off integration / EHR launch surfaces under other path
 * prefixes, so a partner handoff is never hijacked to onboarding.
 */
const GUARDED_ROUTE_PREFIX = '/scribe';

/**
 * Self-heal for users who are authenticated but have no region assigned.
 *
 * A Heidi user record is only created once a region exists in the user's
 * Kinde properties, which only happens after the onboarding country picker
 * on `/scribe/new-user`. If a sign-in flow drops an authenticated user on
 * any other in-app page without a region (e.g. a vendor handoff that lands
 * them on `/scribe` instead of `/scribe/new-user`), `useQueryUser` never
 * fetches — the user is stranded with an infinite loader and 4xx errors and
 * has no way to recover on their own.
 *
 * This guard detects that state (authenticated + Kinde properties resolved +
 * no region) and routes the user to onboarding so they can finish signup.
 *
 * Safe by construction:
 * - Gated behind the `release-growth-region-onboarding-guard` Flagsmith flag
 *   (default OFF) so the redirect can be rolled out gradually and killed
 *   instantly without a deploy.
 * - Only acts once the Kinde-properties query has *successfully* resolved and
 *   is not mid-refetch, so we never redirect on a transient loading state nor
 *   bounce a freshly-onboarded user while the post-country-pick cache
 *   invalidation is still in flight (stale `region: undefined` + `isSuccess`).
 * - Healthy users always have a region, so the guard is a no-op for them.
 * - Only redirects when *no* region string is set at all. A region that is
 *   present but unrecognised by the FE enum (BE/FE drift) is left alone, so we
 *   never hard-loop an already-onboarded user into onboarding.
 * - Keycloak (on-prem) and FHIR auth resolve to a fallback region in
 *   `useQueryKindeProperties`, so they never trigger the redirect.
 * - SSO users (token carries an `sso_connection_id` claim) are excluded
 *   outright. Their region is provisioned by the org's SSO connection, not the
 *   country picker, and they are not expected to see self-serve onboarding —
 *   a region-less SSO user indicates a connection misconfiguration to fix at
 *   the source, not a state the country picker should repair.
 * - Scoped to the `/scribe` app and excludes the onboarding route itself, so
 *   integration/EHR launch surfaces are untouched and there is no redirect
 *   loop; once the user picks a country the region exists and the guard goes
 *   silent.
 */
export function useRequireRegionGuard() {
  const router = useRouter();
  const token = useAuthToken();
  const isGuardEnabled = useIsRegionOnboardingGuardEnabled();
  const { data: kindeProperties, isSuccess, isFetching } = useQueryKindeProperties();
  // Suppress the self-heal only on *confirmed offline* — not on every non-online
  // state. `pending` can be permanent for the very users this guard rescues: a
  // region-less token means Auth0 caches no identity and `refreshTarget(true)`
  // clears the probe target, so with the offline-recording flag on the atom
  // stays `unknown` (→ `pending`) forever. Blocking `pending` would strand
  // exactly the authenticated-but-no-region user we exist to route into
  // onboarding — the deadlock (no region → no target → pending → guard never
  // runs → region never set) both Benny and Hubert flagged.
  //
  // CORRECTED (DSK-536): this used to justify itself with "a genuinely-offline
  // boot (wifi off) resolves to `offline` via the flag-independent
  // `navigator.onLine === false` branch". That is FALSE on macOS. WKWebView pins
  // `navigator.onLine === true` even with wifi off — stated in this repo's own
  // `src/hooks/desktop/useNavigatorOnline.ts` docstring — so that branch is
  // inert there, and with the offline-recording flag off the Rust probe is never
  // armed either (`src-tauri/src/lib.rs` sets no boot-time target). A genuinely
  // offline macOS boot therefore classifies as `online`, and this line does NOT
  // return. Two files asserted opposite things about the same API and the boot
  // routing depended on the false one; that is the bug DSK-536 fixed, by making
  // `/scribe` the boot default so the wrong answer is harmless rather than by
  // making the answer smarter. The consequence for THIS guard is unchanged and
  // acceptable: an offline macOS user with a region never trips it (the guard
  // only fires when no region is set at all), and an offline user with no region
  // has no cached identity, so they could not have got this far authenticated.
  // `pending` still only ever means "browser reports online but the native probe
  // hasn't confirmed yet". (Note we can't lean on
  // the `isSuccess` gate below to prove reachability: `getKindeProperties` is a
  // persisted query, so `isSuccess` can be restored `true` from localStorage
  // while offline.) On web the shared predicate is always `online`, so the guard
  // behaves as before there.
  const desktopConnectivity = useDesktopConnectivity();

  const hasRegion = !!kindeProperties?.region;
  // A region string exists but failed the FE enum allowlist (drift) — distinct
  // from "no region at all". Don't redirect these; that would soft-lock them.
  const hasUnrecognisedRegion = !hasRegion && !!kindeProperties?.rawRegion;
  // SSO users get their region from the org's SSO connection, not the country
  // picker — never route them into self-serve onboarding.
  const isSsoUser = !!token && !!extractSsoConnectionIdFromAccessToken(token);

  useEffect(() => {
    if (!isGuardEnabled) return;
    if (desktopConnectivity === 'offline') return;
    if (!token || !isSuccess || isFetching) return;
    if (hasRegion || hasUnrecognisedRegion || isSsoUser) return;
    if (!router.pathname.startsWith(GUARDED_ROUTE_PREFIX)) return;
    if (router.pathname === ONBOARDING_ROUTE) return;

    void router.replace(ONBOARDING_ROUTE);
  }, [
    isGuardEnabled,
    desktopConnectivity,
    token,
    isSuccess,
    isFetching,
    hasRegion,
    hasUnrecognisedRegion,
    isSsoUser,
    router,
  ]);
}
